/**
 * Centralised map of Web Intelligence Ad Intelligence state names → their Sales Intelligence
 * equivalents.
 *
 * When a SI page reuses a Web Intelligence component, that component is unaware of the SI
 * solution and tries to navigate to its native WI state. While SI is mounted, a scoped
 * `onBefore` transition hook (see {@link ./../utils/redirectWiToSi}) looks up the target WI
 * state name in this map and rewrites the transition to the SI equivalent so the user stays
 * inside SI.
 *
 * Add a new entry whenever a SI page mirrors another WI Ad Intelligence state.
 */
import {
    SI_AA_ROUTES,
    SI_DISPLAY_ROUTES,
    SI_LLMS_ROUTES,
    SI_MNP_ROUTES,
    SI_MNP_TRADEMARK_ROUTES,
    SI_PA_ROUTES,
    SI_SEARCH_ROUTES,
    SI_SOCIAL_ROUTES,
} from "app/common/constants/ad-intelligence-routes";
import {
    AI_CHANNELS_ROUTES as WI_AI_ROUTES,
    SOCIAL_CHANNELS_ROUTES as WI_SOCIAL_ROUTES,
} from "app/pages/digital-marketing/ad-intelligence";
import {
    MONITOR_AND_PROTECT_ROUTES as WI_MNP_ROUTES,
    TRADEMARK_ROUTES as WI_MNP_TRADEMARK_ROUTES,
} from "app/pages/digital-marketing/monitor-and-protect";
import { ROUTES as WI_AA_ROUTES } from "single-spa/react-app/config/solutions25/adIntelligence/advertiserActivity/advertiserActivityConstants";
import { ROUTES as WI_DISPLAY_ROUTES } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingConstants";
import { ROUTES as WI_PA_ROUTES } from "single-spa/react-app/config/solutions25/adIntelligence/publisherAnalysis/publisherAnalysisConstants";
import { ROUTES as WI_SEARCH_ROUTES } from "single-spa/react-app/config/solutions25/adIntelligence/searchAdvertising/searchAdvertisingConstants";

export const WI_TO_SI_AD_INTEL_ROUTES: Readonly<Record<string, string>> = Object.freeze<Record<string, string>>({
    // Advertiser Activity
    [WI_AA_ROUTES.home]: SI_AA_ROUTES.home,
    [WI_AA_ROUTES.overview]: SI_AA_ROUTES.overview,

    // Display Advertising
    // WI_DISPLAY_ROUTES entries are route descriptor objects, not bare state names,
    // so the state name has to be read explicitly (see displayAdvertisingConstants).
    [WI_DISPLAY_ROUTES.overview.stateName]: SI_DISPLAY_ROUTES.overview,
    [WI_DISPLAY_ROUTES.campaigns.stateName]: SI_DISPLAY_ROUTES.campaigns,
    [WI_DISPLAY_ROUTES.ads.stateName]: SI_DISPLAY_ROUTES.ads,
    [WI_DISPLAY_ROUTES.publishers.stateName]: SI_DISPLAY_ROUTES.publishers,

    // Search Advertising
    [WI_SEARCH_ROUTES.monitorKeywordsHome]: SI_SEARCH_ROUTES.monitorKeywordsHome,
    [WI_SEARCH_ROUTES.monitorKeywords]: SI_SEARCH_ROUTES.monitorKeywords,
    [WI_SEARCH_ROUTES.keywordLandingPages]: SI_SEARCH_ROUTES.keywordLandingPages,
    [WI_SEARCH_ROUTES.serpPlayers]: SI_SEARCH_ROUTES.serpPlayers,
    [WI_SEARCH_ROUTES.keywordAds]: SI_SEARCH_ROUTES.keywordAds,
    [WI_SEARCH_ROUTES.topics]: SI_SEARCH_ROUTES.topics,
    [WI_SEARCH_ROUTES.keywords]: SI_SEARCH_ROUTES.keywords,
    [WI_SEARCH_ROUTES.landingPages]: SI_SEARCH_ROUTES.landingPages,
    [WI_SEARCH_ROUTES.textAndShoppingAds]: SI_SEARCH_ROUTES.textAndShoppingAds,

    // Social Channels
    [WI_SOCIAL_ROUTES.ADS.stateName]: SI_SOCIAL_ROUTES.ads,

    // AI Channels — SI has no AI ads gallery, so the WI gallery maps onto the SI promo page.
    [WI_AI_ROUTES.ADS.stateName]: SI_LLMS_ROUTES.promo,

    // Publisher Analysis
    [WI_PA_ROUTES.home]: SI_PA_ROUTES.home,
    [WI_PA_ROUTES.overview]: SI_PA_ROUTES.overview,
    [WI_PA_ROUTES.advertisers]: SI_PA_ROUTES.advertisers,
    [WI_PA_ROUTES.ads]: SI_PA_ROUTES.ads,

    // Monitor & Protect — Keywords
    [WI_MNP_ROUTES.ABSTRACT.stateName]: SI_MNP_ROUTES.ABSTRACT.stateName,
    [WI_MNP_ROUTES.HOME.stateName]: SI_MNP_ROUTES.HOME.stateName,
    [WI_MNP_ROUTES.OVERVIEW.stateName]: SI_MNP_ROUTES.OVERVIEW.stateName,
    [WI_MNP_ROUTES.KEYWORDS.stateName]: SI_MNP_ROUTES.KEYWORDS.stateName,
    [WI_MNP_ROUTES.ADS.stateName]: SI_MNP_ROUTES.ADS.stateName,
    [WI_MNP_ROUTES.SETTINGS.stateName]: SI_MNP_ROUTES.SETTINGS.stateName,
    [WI_MNP_ROUTES.COMPETITORS.stateName]: SI_MNP_ROUTES.COMPETITORS.stateName,

    // Monitor & Protect — Trademark
    [WI_MNP_TRADEMARK_ROUTES.ABSTRACT.stateName]: SI_MNP_TRADEMARK_ROUTES.ABSTRACT.stateName,
    [WI_MNP_TRADEMARK_ROUTES.VIOLATIONS.stateName]: SI_MNP_TRADEMARK_ROUTES.VIOLATIONS.stateName,
    [WI_MNP_TRADEMARK_ROUTES.TRADEMARKS.stateName]: SI_MNP_TRADEMARK_ROUTES.TRADEMARKS.stateName,
    [WI_MNP_TRADEMARK_ROUTES.TRADEMARKS_CREATE.stateName]: SI_MNP_TRADEMARK_ROUTES.TRADEMARKS_CREATE.stateName,
    [WI_MNP_TRADEMARK_ROUTES.TRADEMARKS_EDIT.stateName]: SI_MNP_TRADEMARK_ROUTES.TRADEMARKS_EDIT.stateName,
});
